import { ApplicationRef, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { Event, NavigationCancel, NavigationError, NavigationSkipped, NavigationStart, Router, Scroll } from '@angular/router';
import { from, Subscription, switchMap } from 'rxjs';

interface NavigationWindow {
  readonly id: number;
  readonly start: number;
  // Null while the navigation is still in flight.
  readonly end: number | null;
}

// A route swap can paint a frame with the old page gone and the new one not yet rendered, which puts the footer in view.
// An intersection measured during a navigation is therefore checked again once the navigation ends.
@Injectable()
export class FooterHydrationTriggerService {
  private readonly router = inject(Router);
  private readonly inView = signal(false);
  private readonly navigations: Subscription;

  public readonly footerInView = this.inView.asReadonly();

  private navigation: NavigationWindow | null = null;
  private observer: IntersectionObserver | null = null;
  private footer: Element | null = null;
  private sawIntersectionWhileNavigating = false;

  constructor() {
    // The router sends no Scroll event for a navigation that ends before the app is first stable,
    // so tracking starts there and every navigation it sees is closed by one.
    this.navigations = from(inject(ApplicationRef).whenStable())
      .pipe(switchMap(() => this.router.events))
      .subscribe((event) => this.trackNavigation(event));

    inject(DestroyRef).onDestroy(() => this.stop());
  }

  public observe(footer: Element | null): void {
    if (!footer) {
      return;
    }

    if (typeof IntersectionObserver === 'undefined') {
      this.inView.set(true);
      return;
    }

    this.footer = footer;
    this.observer = new IntersectionObserver((entries) => this.checkIntersection(entries));
    this.observer.observe(footer);
  }

  private checkIntersection(entries: IntersectionObserverEntry[]): void {
    // A delayed callback can batch an old entry with newer ones, and only the newest is the footer's current state.
    const latest = newestEntry(entries);
    if (!latest?.isIntersecting) {
      return;
    }

    const navigation = this.navigation;
    if (navigation !== null && navigation.end === null) {
      this.sawIntersectionWhileNavigating = true;
      return;
    }

    // A long task, such as a replayed hydration, can delay this callback past the end of the navigation the entry was measured in.
    if (navigation !== null && navigation.end !== null && latest.time >= navigation.start && latest.time <= navigation.end) {
      this.reobserve();
      return;
    }

    this.inView.set(true);
    // The signal never goes back to false, so nothing after this needs the observer or the router.
    this.stop();
  }

  private trackNavigation(event: Event): void {
    if (event instanceof NavigationStart) {
      // A navigation that supersedes one still open keeps the earlier start, so its window covers both.
      const start = this.navigation?.end === null ? this.navigation.start : performance.now();
      this.navigation = { id: event.id, start, end: null };
      return;
    }

    // A late Scroll from an earlier navigation must not end the one in flight.
    if (!this.navigation || this.navigation.end !== null || endedNavigationId(event) !== this.navigation.id) {
      return;
    }

    this.navigation = { ...this.navigation, end: performance.now() };

    if (this.sawIntersectionWhileNavigating) {
      this.sawIntersectionWhileNavigating = false;
      this.reobserve();
    }
  }

  // The observer only reports crossings, so a footer still in view on a short page would never report again.
  // Observing afresh makes it measure the settled page.
  private reobserve(): void {
    if (!this.observer || !this.footer) {
      return;
    }

    this.observer.unobserve(this.footer);
    this.observer.observe(this.footer);
  }

  private stop(): void {
    this.navigations.unsubscribe();
    this.observer?.disconnect();
    this.observer = null;
    this.footer = null;
  }
}

function newestEntry(entries: IntersectionObserverEntry[]): IntersectionObserverEntry | undefined {
  return entries.reduce<IntersectionObserverEntry | undefined>(
    (newest, entry) => (!newest || entry.time > newest.time ? entry : newest),
    undefined,
  );
}

// Scroll (from withInMemoryScrolling) comes a task after NavigationEnd and restores the scroll position,
// so closing on it keeps the navigation open past the half-swapped frame.
function endedNavigationId(event: Event): number | null {
  if (event instanceof Scroll) {
    return event.routerEvent.id;
  }

  if (event instanceof NavigationCancel || event instanceof NavigationError || event instanceof NavigationSkipped) {
    return event.id;
  }

  return null;
}
